import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';
import type { HistoryResult } from '../types';

interface Point {
  i: number;
  score: number;
  time: string;
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: { payload: Point }[] }): JSX.Element | null {
  if (!active || !payload?.length) return null;
  const p = payload[0]!.payload;
  return (
    <div className="bg-warm-dark border border-warm-dark-2 rounded px-2 py-1 font-mono text-sm">
      <div className="text-text-secondary">{p.time}</div>
      <div style={{ color: '#00FFD4' }}>score {p.score}</div>
    </div>
  );
}

export default function Timeline({
  history,
  onRescan,
  rescanning,
}: {
  history: HistoryResult;
  onRescan: () => void;
  rescanning: boolean;
}): JSX.Element | null {
  const { scans, diff } = history;
  if (scans.length === 0) return null;

  // Chronological (oldest → newest) for the sparkline.
  const series: Point[] = [...scans]
    .reverse()
    .map((s, i) => ({ i, score: s.score, time: new Date(s.createdAt).toLocaleString() }));
  const latest = new Date(scans[0]!.createdAt).toLocaleString();

  return (
    <div className="w-full max-w-5xl mx-auto border border-warm-dark-2 rounded-xl p-5">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="font-mono text-sm uppercase tracking-widest text-text-secondary">
          Trend ({scans.length} {scans.length === 1 ? 'scan' : 'scans'})
        </h2>
        <button
          onClick={onRescan}
          disabled={rescanning}
          className="font-mono text-sm border border-cyan/40 text-cyan rounded px-3 py-1 disabled:opacity-50 hover:bg-cyan/10 transition"
        >
          {rescanning ? 'measuring…' : '↻ re-scan'}
        </button>
      </div>
      <p className="font-mono text-sm text-text-secondary mb-2">latest: {latest} · hover a point for its timestamp</p>

      {series.length > 1 && (
        <div className="h-16 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <YAxis domain={[0, 100]} hide />
              <Tooltip content={<TrendTooltip />} cursor={{ stroke: '#333' }} />
              <Line type="monotone" dataKey="score" stroke="#00FFD4" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {diff ? (
        <div className="font-mono text-sm text-text-secondary">
          <span>vs previous: </span>
          <span style={{ color: diff.scoreDelta > 0 ? '#00FFD4' : diff.scoreDelta < 0 ? '#E0574A' : '#A0A0AC' }}>
            {diff.scoreDelta > 0 ? '+' : ''}
            {diff.scoreDelta} pts
          </span>
          {diff.resolved.length > 0 && <span className="text-measured"> · {diff.resolved.length} resolved</span>}
          {diff.added.length > 0 && <span className="text-estimated"> · {diff.added.length} new</span>}
          {diff.scoreDelta === 0 && diff.resolved.length === 0 && diff.added.length === 0 && <span> · no change</span>}
        </div>
      ) : (
        <p className="font-mono text-sm text-text-secondary">
          First scan. Re-scan after editing the page to see the change.
        </p>
      )}
    </div>
  );
}
